"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
exports.__esModule = true;
exports.NodeStore = void 0;
var attrStore_1 = require("./attrStore");
var deferredResult_1 = require("./deferredResult");
/**
 * A NodeStore contains stateful information about a Prosemirror node. It keeps no reference to
 * any `Node` object found in a document (which are transient and disposable anyway) and is intended
 * intended to be mapped by its caller to a stable node ID. This class is intended to be
 * instantiated by `DocumentStore`.
 */
var NodeStore = /** @class */ (function () {
    /**
     * Create a NodeStore
     * @param definition a set of `Node` attributes associated with hooks that will be used to
     *   derive their values.
     */
    function NodeStore(definition, parentStore) {
        this.attrStores = {};
        this.attrStates = {};
        this.parentStore = parentStore;
        this.invalidateAttr = this.invalidateAttr.bind(this);
        var entries = Object.entries(definition);
        for (var i = 0; i < entries.length; i++) {
            var _a = __read(entries[i], 2), attrName = _a[0], attrFn = _a[1];
            this.attrStores[attrName] = new attrStore_1.AttrStore(attrName, attrFn, parentStore.getHooks(), this.invalidateAttr);
        }
    }
    NodeStore.prototype.invalidateAttr = function (attr) {
        var possiblyDeferredResult = this.attrStores[attr].run(this.reactiveCopy);
        var result = possiblyDeferredResult instanceof deferredResult_1.DeferredResult
            ? possiblyDeferredResult.callback(this.parentStore.getAvailableNodes())
            : possiblyDeferredResult;
        var attrChanged = this.updateAttrState(attr, result);
        if (attrChanged) {
            this.parentStore.onInvalidateNode(this.reactiveCopy);
        }
    };
    NodeStore.prototype.updateReactiveCopy = function (node) {
        this.reactiveCopy = node.copy();
        this.reactiveCopy.attrs = __assign(__assign({}, this.reactiveCopy.attrs), this.attrStates);
    };
    NodeStore.prototype.updateAttrState = function (attr, value) {
        var previous = this.attrStates[attr];
        var attrChanged = value !== previous;
        this.attrStates[attr] = value;
        this.reactiveCopy.attrs[attr] = value;
        return attrChanged;
    };
    NodeStore.prototype.getDeferredResult = function (partialHasNodeChanged, deferredAttrResults) {
        var _this = this;
        var entries = Object.entries(deferredAttrResults);
        var requestedNodeIds = new Set();
        var callbacks = [];
        for (var i = 0; i < entries.length; i++) {
            var _a = __read(entries[i], 2), _ = _a[0], deferredResult = _a[1];
            deferredResult.requestedNodeIds.forEach(function (id) { return requestedNodeIds.add(id); });
            callbacks.push(deferredResult.callback);
        }
        return new deferredResult_1.DeferredResult(requestedNodeIds, function (requestedNodes) {
            var hasNodeChanged = partialHasNodeChanged;
            for (var i = 0; i < callbacks.length; i++) {
                var attr = entries[i][0];
                var nextValue = callbacks[i](requestedNodes);
                var hasAttrChanged = _this.updateAttrState(attr, nextValue);
                hasNodeChanged = hasNodeChanged || hasAttrChanged;
            }
            return [hasNodeChanged, _this.reactiveCopy];
        });
    };
    /**
     * Get the reactive copy of the Node computed by this store.
     */
    NodeStore.prototype.getReactiveCopy = function () {
        return this.reactiveCopy;
    };
    NodeStore.prototype.destroy = function () {
        Object.values(this.attrStores).forEach(function (store) { return store.destroy(); });
    };
    /**
     * Updates the reactive store for every reactive attribute associated with this `Node`.
     * Returns a boolean indicating whether this run changed the attributes associated with this
     * Node, or a `DeferredResult` requesting a reference to some other `Node` instances.
     * @param node The node to update against.
     * @param documentState Global, mutable state for the document
     * @param transactionState Global, mutable state for the current transaction
     */
    NodeStore.prototype.run = function (node) {
        this.updateReactiveCopy(node);
        var results = {};
        for (var key in this.attrStores) {
            var store = this.attrStores[key];
            results[key] = store.run(node);
        }
        var hasNodeChanged = false;
        var deferredResults = {};
        for (var attr in results) {
            var result = results[attr];
            if (result instanceof deferredResult_1.DeferredResult) {
                deferredResults[attr] = result;
            }
            else {
                var hasAttrChanged = this.updateAttrState(attr, result);
                hasNodeChanged = hasNodeChanged || hasAttrChanged;
            }
        }
        if (Object.keys(deferredResults).length > 0) {
            return this.getDeferredResult(hasNodeChanged, deferredResults);
        }
        else {
            return [hasNodeChanged, this.reactiveCopy];
        }
    };
    return NodeStore;
}());
exports.NodeStore = NodeStore;
